import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect, useSelector } from 'react-redux'
import { Layout } from 'frame'
import { Page } from 'frame/components'
import {
  checkHaveAppUsers,
  fetchSOITypes,
  submitSOIRegistration
} from '../actions'
import { RegisterInterestForm } from './RegisterInterest'
import MyFleetSummary from './my-fleet-summary'
import MyNotifications from './my-notfications'
import StatusUpdates from './status-updates'
import IndustryNews from './industry-news'
import Downloads from './downloads/downloads'
import Footer from './footer'
import PromoCard from './promo-card'
import Grid from '@mui/material/Grid'
import PartnersGrid from './partners-grid'
import { useEnabledFeatureToggleList } from 'features/settings'
import ContactInformation from './contact-information'
import FuelCardPromo from './fuel-card-promo'
import StyledModalV2 from 'frame/components/styled-modal-v2'
import { servicesCatalog } from '../config'
import { PageLoader } from 'frame/components'
import Typography from '@mui/material/Typography'

const SummaryDashboard = ({
  haveAppUsers,
  onCheckHaveAppUsers,
  onFetchSOITypes,
  onSubmitSOI,
}) => {
  const showPromoCard = !haveAppUsers
  const userEmail = useSelector(({ account }) => account.profile?.email)
  const userName = useSelector(({ account }) => account.profile?.nickname)
  const [loading, setLoading] = useState(false)
  const [interestOpen, setInterestOpen] = useState(false)
  const [interestValues, setInterestValues] = useState({
    name: userName,
    email: userEmail,
    mobile: '',
    comments: '',
    services: [],
  })

  const [showConfirmation, setShowConfirmation] = useState(false)
  const [showError, setShowError] = useState(false)
  const [apiResponse, setApiResponse] = useState('')

  const featureToggleList = useEnabledFeatureToggleList()
  const isTfp2538FeatureToggleOn = featureToggleList.includes(
    'Services of Interest Registration (TFP-2538)'
  )

  const handleClose = () => {
    setInterestValues({
      name: userName,
      email: userEmail,
      mobile: '',
      comments: '',
      services: [],
    })
    setInterestOpen(false)
  }

  useEffect(() => {
    onCheckHaveAppUsers()
    onFetchSOITypes()
  }, [userEmail, onCheckHaveAppUsers, onFetchSOITypes])

  const handleApply = () => setInterestOpen(true)

  const handleSave = async () => {
    setLoading(true) // start loader

    try {
      const result = await onSubmitSOI({
        email: interestValues.email,
        name: interestValues.name,
        mobile: interestValues.mobile,
        comments: interestValues.comments,
        servicesOfInterest: interestValues.services,
      })

      console.log('handleSave result:', JSON.stringify(result, null, 2))

      const { apiResponse } = result || {}
      const { success, message } = apiResponse || {}

      if (success) {
        setApiResponse(
          message || 'Your request has been successfully sent to TFM, and someone will be in touch shortly to discuss your options.'
        )
        setShowConfirmation(true)
        setInterestOpen(false)
      } else {
        setApiResponse(message || 'Something went wrong. Please try again.')
        setShowError(true)
      }
    } catch (error) {
      console.error('handleSave error:', error)
      setApiResponse('Something went wrong. Please try again.')
      setShowError(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Layout backgroundImage="url('/static/images/backgrounds/bg1.png')">
      {loading && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.6)', // translucent overlay
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 20000, // ensures it sits above all modals / content
          }}>
          <PageLoader />
        </div>
      )}
      <Page title="My Fleet Summary Dashboard" sx={{ p: 3 }}>
        <Grid
          container
          sx={{
            position: 'relative',
            '& > .MuiGrid-item': {
              display: 'flex',
              flexDirection: 'column',
            },
          }}>
          <Grid item xs={12} sx={{ paddingBottom: 1 }}>
            <MyFleetSummary />
          </Grid>
          {/* 2nd Row */}
          <Grid item xs={12} sm={12} md={6} lg={4} sx={{ paddingY: 1, paddingRight: 1 }}>
            {showPromoCard ? <PromoCard /> : <MyNotifications />}
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={4} sx={{ paddingX: 1, paddingTop: 1 }}>
            <StatusUpdates />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={4} sx={{ paddingTop: 1, paddingLeft: 1 }}>
            <IndustryNews />
          </Grid>

          {/* 3rd Row */}
          <Grid item xs={12} sm={12} md={6} lg={4} sx={{ paddingTop: 1, paddingRight: 1 }}>
            <Downloads />
          </Grid>

          {isTfp2538FeatureToggleOn ? (
            <Grid item xs={12} sm={12} md={6} lg={8} sx={{ paddingTop: 1, paddingLeft: 1 }}>
              <PartnersGrid onApply={handleApply} />
            </Grid>
          ) : (
            <>
              <Grid item xs={12} md={6} lg={4} sx={{ paddingTop: 1, paddingX: 1 }}>
                <FuelCardPromo />
              </Grid>
              <Grid item xs={12} md={6} lg={4} sx={{ paddingTop: 1, paddingLeft: 1 }}>
                <ContactInformation />
              </Grid>
            </>
          )}
        </Grid>
      </Page>
      {loading && <PageLoader />}
      <RegisterInterestForm
        open={interestOpen}
        values={interestValues}
        setValues={setInterestValues}
        onClose={handleClose}
        onSave={handleSave}
        servicesCatalog={servicesCatalog}/>

      {/* StyledModalV2 for success/error */}
      <StyledModalV2
        open={showConfirmation || showError}
        onClose={() => {
          setShowConfirmation(false)
          setShowError(false)
          handleClose()
        }}
        status={showConfirmation ? 'success' : 'error'}
        content={
          <Typography variant="h4" textAlign="center">
            {apiResponse}
          </Typography>
        }/>

      <Footer />
    </Layout>
  )
}

SummaryDashboard.propTypes = {
  haveAppUsers: PropTypes.bool,
  onCheckHaveAppUsers: PropTypes.func.isRequired,
  onFetchSOITypes: PropTypes.func.isRequired,
  onSubmitSOI: PropTypes.func.isRequired,
}

const mapStateToProps = ({ homepage }) => ({
  haveAppUsers: homepage?.haveAppUsers,
})

const mapDispatchToProps = (dispatch) => ({
  onCheckHaveAppUsers: () => dispatch(checkHaveAppUsers()),
  onFetchSOITypes: () => dispatch(fetchSOITypes()),
  onSubmitSOI: (payload) =>
    new Promise((resolve) => {
      dispatch(submitSOIRegistration(payload, { resolve }))
    }),
})

export default connect(mapStateToProps, mapDispatchToProps)(SummaryDashboard)
